import React, {
  createContext,
  useContext,
  Dispatch,
  useEffect,
  useMemo,
} from 'react';
import { useAvailableFilterState } from './StateProvider';
import { useAddStartFilterMetric } from '../../../Shared/useAddStartFilterMetric';
import { AirportType } from './SummaryProvider';
import { useSelectedUnbundleDepartureId } from 'clientState/providers/SelectedUnbundleDepartureProvider';

type ActionType = {
  type: string;
  value?: string;
  legId?: number;
  airportType?: string;
  availableAirports?: AirportType[];
  isNewSearch?: boolean;
};

export const ActionsContext = createContext<Dispatch<ActionType>>(() => {});

const toggleReducer = (airportItems, { value }) => {
  return airportItems.map((airport) =>
    airport.id !== value
      ? airport
      : {
          ...airport,
          checked: !airport.checked,
        }
  );
};

const selectOneReducer = (airportItems, { value }) =>
  airportItems.map((airport) => ({
    ...airport,
    checked: airport.id === value,
  }));

const createApplyAll = (checked) => (airportItems) => {
  return airportItems.map((airport) => ({
    ...airport,
    checked: airport?.disabled ? airport?.checked : checked,
  }));
};

const addSpecificSearch = (airports, { isNewSearch }) => ({
  airports,
  isNewSearch,
});

const createLegReducer = (itemsReducer) => (state, action) => {
  const { airports } = state || action.availableAirports;
  const { legId, airportType } = action;
  const nextAirports = airports.map((legAirports, index) => {
    return legId !== index
      ? legAirports
      : {
          ...legAirports,
          [airportType]: itemsReducer(legAirports[airportType], action),
        };
  });
  return addSpecificSearch(nextAirports, action);
};

const createApplyAllReducer = (itemsReducer) => (state, action) => {
  const { airports } = state || action.availableAirports;
  const nextAirports = airports.map((legAirports, legId) => ({
    id: legId,
    departureAirports: itemsReducer(legAirports.departureAirports, action),
    destinationAirports: itemsReducer(legAirports.destinationAirports, action),
  }));
  return addSpecificSearch(nextAirports, action);
};

const legToggleReducer = createLegReducer(toggleReducer);
const legSelectOneReducer = createLegReducer(selectOneReducer);
const legSelectAllReducer = createLegReducer(createApplyAll(true));
const legDeselectAllReducer = createLegReducer(createApplyAll(false));
const selectAllReducer = createApplyAllReducer(createApplyAll(true));

export const reducer = (airports, action) => {
  switch (action.type) {
    case 'INIT':
      return null;
    case 'LEG_TOGGLE':
      return legToggleReducer(airports, action);
    case 'LEG_SELECT_ONE':
      return legSelectOneReducer(airports, action);
    case 'LEG_SELECT_ALL':
      return legSelectAllReducer(airports, action);
    case 'LEG_DESELECT_ALL':
      return legDeselectAllReducer(airports, action);
    case 'SELECT_ALL':
      return selectAllReducer(airports, action);
    default:
      return airports;
  }
};

export const useLegFilterActions = (legId, airportType) => {
  const addStartMetric = useAddStartFilterMetric();
  const availableAirports = useAvailableFilterState();
  const dispatch = useContext(ActionsContext);

  return useMemo(() => {
    const createAction = (props) => ({
      legId,
      airportType,
      availableAirports,
      ...props,
    });
    return {
      toggle: addStartMetric(({ value }) =>
        dispatch(
          createAction({
            type: 'LEG_TOGGLE',
            value,
          })
        )
      ),
      selectOne: addStartMetric((value) =>
        dispatch(
          createAction({
            type: 'LEG_SELECT_ONE',
            value,
          })
        )
      ),
      selectAll: addStartMetric(() =>
        dispatch(
          createAction({
            type: 'LEG_SELECT_ALL',
          })
        )
      ),
      deselectAll: addStartMetric(() =>
        dispatch(
          createAction({
            type: 'LEG_DESELECT_ALL',
          })
        )
      ),
    };
  }, [dispatch, availableAirports, airportType, legId, addStartMetric]);
};

export const useFilterActions = () => {
  const addStartMetric = useAddStartFilterMetric();
  const availableAirports = useAvailableFilterState();
  const dispatch = useContext(ActionsContext);
  return useMemo(() => {
    return {
      selectAll: addStartMetric(({ isNewSearch = false } = {}) => {
        dispatch({
          type: 'SELECT_ALL',
          availableAirports,
          isNewSearch,
        });
      }),
      selectLegAll: addStartMetric(({ legId, airportType }) => {
        dispatch({
          type: 'LEG_SELECT_ALL',
          legId,
          airportType,
          availableAirports,
        });
      }),
    };
  }, [dispatch, availableAirports, addStartMetric]);
};

const ActionsProvider = ({ dispatch, children }) => {
  const availableAirports = useAvailableFilterState();
  const selectedUnbundleDepartureId = useSelectedUnbundleDepartureId();
  useEffect(() => {
    if (!availableAirports) {
      dispatch({ type: 'INIT' });
    }
  }, [dispatch, availableAirports]);

  // Reset the state when the unbundle page changes so it
  // refreshes the available airports
  useEffect(() => {
    dispatch({ type: 'INIT' });
  }, [selectedUnbundleDepartureId, dispatch]);
  return (
    <ActionsContext.Provider value={dispatch}>
      {children}
    </ActionsContext.Provider>
  );
};

export default ActionsProvider;
